import { useState } from "react";

import { Button, type Column, EmptyState, Pill, Table } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { InviteModal } from "@/components/invitations/InviteModal";
import { useRevokeUserInvitation, useUserInvitations } from "@/hooks/useInvitations";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { InvitationListItem, InvitationType } from "@/types/invitation";
import { formatDate } from "@/utils/fmt";

const STATUS_TONE: Record<string, "ativa" | "pausada" | "diag"> = {
  pending: "diag",
  accepted: "ativa",
  revoked: "pausada",
  expired: "pausada",
};
const STATUS_LABEL: Record<string, string> = {
  pending: "Pendente",
  accepted: "Aceito",
  revoked: "Revogado",
  expired: "Expirado",
};

export function InvitesTab({ workspaceId }: { workspaceId: number }) {
  const { data: invites = [], isLoading } = useUserInvitations(workspaceId);
  const revoke = useRevokeUserInvitation(workspaceId);
  const [modalKind, setModalKind] = useState<InvitationType | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  async function onRevoke(inv: InvitationListItem) {
    if (inv.status !== "pending") return;
    if (!confirm("Revogar este convite?")) return;
    setRevokeError(null);
    try {
      await revoke.mutateAsync(inv.id);
    } catch (err) {
      setRevokeError(extractErrorMessage(err));
    }
  }

  const columns: Column<InvitationListItem>[] = [
    {
      id: "email",
      header: "Convidado",
      cell: (i) => <span className="razao">{i.email_masked}</span>,
    },
    { id: "scope", header: "Escopo", cell: (i) => <span className="td-small">{i.scope ?? "—"}</span> },
    {
      id: "status",
      header: "Status",
      cell: (i) => <Pill tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</Pill>,
    },
    {
      id: "expires",
      header: "Expira",
      cell: (i) => <span className="td-small">{formatDate(i.expires_at)}</span>,
    },
    {
      id: "actions",
      header: "",
      cell: (i) =>
        i.status === "pending" ? (
          <Guard permission="users.invitations.revoke">
            <Button variant="ghost" iconLeft="x" onClick={() => void onRevoke(i)}>
              Revogar
            </Button>
          </Guard>
        ) : null,
    },
  ];

  return (
    <>
      <div className="invites-toolbar">
        <Guard permission="users.invitations.create">
          <Button variant="primary" iconLeft="plus" onClick={() => setModalKind("user")}>
            Convidar usuário
          </Button>
        </Guard>
        <Guard permission="organizations.invitations.create">
          <Button variant="ghost" iconLeft="mail" onClick={() => setModalKind("organization")}>
            Convidar organização
          </Button>
        </Guard>
        <Guard permission="clients.write">
          <Button variant="ghost" iconLeft="mail" onClick={() => setModalKind("client")}>
            Convidar empresa
          </Button>
        </Guard>
      </div>

      {revokeError && (
        <div className="form-error" role="alert" style={{ marginBottom: 12 }}>
          {revokeError}
        </div>
      )}

      <Table
        rows={invites}
        columns={columns}
        getKey={(i) => String(i.id)}
        empty={
          <EmptyState
            icon="mail"
            title={isLoading ? "Carregando…" : "Nenhum convite de usuário"}
            description={isLoading ? undefined : "Convide o primeiro membro do workspace."}
          />
        }
      />

      {modalKind && (
        <InviteModal kind={modalKind} workspaceId={workspaceId} onClose={() => setModalKind(null)} />
      )}
    </>
  );
}
